import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  Icon,
  Skeleton,
  Stack,
  Table,
  icons,
  type TableColumn,
} from '@agentisai/design-system';
import { EVAL_SET_NAME, type EvalSetSummary } from '@agentisai/shared-types';
import { ApiError } from '../../queries/client';
import { useEvalSet, useEvalSets } from '../../queries/useEvalFixtures';
import { formatBytes, muted } from './format';
import { SetEditor } from './SetEditor';
import { toDraft } from './setDraft';

/** `?set=` — the open question set, URL-owned (R3) so an editor is linkable. */
const SET_PARAM = 'set';

function OpenSet({
  name,
  canWrite,
  onClose,
}: {
  name: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const query = useEvalSet(name, true);
  if (query.isPending) return <Skeleton shape="card" height={320} />;
  if (query.isError) {
    return (
      <div role="alert">
        <Callout tone="danger">
          {query.error instanceof ApiError && query.error.status === 404
            ? `There is no set called ${name}.`
            : query.error instanceof ApiError && query.error.status === 422
              ? `The stored set ${name} isn't valid (${query.error.message}).`
              : `Couldn't load ${name}.`}
        </Callout>
      </div>
    );
  }
  const { set, etag } = query.data.data;
  return (
    <SetEditor
      key={etag}
      initial={toDraft(set)}
      etag={etag}
      canWrite={canWrite}
      onSaved={() => undefined}
      onClose={onClose}
    />
  );
}
OpenSet.displayName = 'OpenSet';

/**
 * Question sets (ADR-0073): the cases `evals/run.py --set <name>` runs. List,
 * create, edit, tag and delete them here; the runner reads the same objects
 * from the eval-fixtures bucket. Edit controls show only with `evals:write`;
 * the BFF enforces it, and every save is conditional on the copy it was read from.
 */
export function EvalSetsPanel({ canWrite }: { canWrite: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const sets = useEvalSets(true);
  // Validated at the boundary (R3): a hand-edited ?set= never keys a request.
  const rawSet = searchParams.get(SET_PARAM);
  const open = rawSet && EVAL_SET_NAME.test(rawSet) ? rawSet : undefined;
  const openSet = (name: string) => setSearchParams({ tab: 'sets', [SET_PARAM]: name });
  const closeSet = () => {
    setCreating(false);
    setSearchParams({ tab: 'sets' });
  };

  if (creating) {
    return (
      <SetEditor
        initial={toDraft(null)}
        etag={null}
        canWrite={canWrite}
        onSaved={(name) => {
          setCreating(false);
          openSet(name);
        }}
        onClose={closeSet}
      />
    );
  }
  if (open) return <OpenSet name={open} canWrite={canWrite} onClose={closeSet} />;

  const columns: TableColumn<EvalSetSummary>[] = [
    { key: 'name', header: 'Set' },
    { key: 'sizeBytes', header: 'Size', render: (s) => formatBytes(s.sizeBytes) },
    {
      key: 'lastModified',
      header: 'Updated',
      render: (s) => new Date(s.lastModified).toLocaleString('en-AU'),
    },
    {
      key: 'open',
      header: 'Open',
      ariaLabel: 'Open set',
      render: (s) => (
        <Button
          variant="link"
          size="sm"
          aria-label={`Open ${s.name}`}
          onClick={() => openSet(s.name)}
        >
          {canWrite ? 'Edit' : 'View'}
        </Button>
      ),
    },
  ];

  return (
    <Stack direction="column" gap={4}>
      <Stack direction="row" gap={3} align="center" justify="between" wrap>
        <span style={muted}>
          Your own question sets, tagged and with documents attached. Each case is a fresh
          conversation.
        </span>
        {canWrite && (
          <Button
            variant="secondary"
            leftIcon={<Icon as={icons.Plus} size="sm" />}
            onClick={() => setCreating(true)}
          >
            New question set
          </Button>
        )}
      </Stack>
      {!canWrite && (
        <Badge tone="neutral" size="sm">
          Read-only: editing needs evals:write
        </Badge>
      )}
      {sets.isPending && <Skeleton shape="card" height={160} />}
      {sets.isError && (
        <div role="alert">
          <Callout tone="danger">
            Couldn't load question sets. Reload the page to try again.
          </Callout>
        </div>
      )}
      {sets.isSuccess &&
        (sets.data.data.sets.length === 0 ? (
          <EmptyState
            icon={<Icon as={icons.FileText} size="lg" />}
            title="No question sets yet"
            description="Create one here, or upload a JSON set to the eval-fixtures bucket."
          />
        ) : (
          <Table
            caption="Question sets"
            rows={sets.data.data.sets}
            columns={columns}
            getRowId={(s) => s.name}
            density="compact"
          />
        ))}
    </Stack>
  );
}
EvalSetsPanel.displayName = 'EvalSetsPanel';
